<template>
  <div
    class="barn-silos-section"
    :class="{ 'barn-silos-section--compact': hideHeader }"
  >
    <div v-if="!hideHeader" class="barn-title-container">
      <h4 class="barn-title">{{ barnTitle }}</h4>
      <button
        class="excel-button"
        @click="downloadProfileReport"
        :disabled="loadingExcel || feedSensors.length === 0"
        title="Baixar Histórico de Perfis"
      >
        <i :class="loadingExcel ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
      </button>
    </div>

    <div class="barn-section-body">
      <div v-if="hideHeader" class="barn-actions-row">
        <button
          class="excel-button"
          @click="downloadProfileReport"
          :disabled="loadingExcel || feedSensors.length === 0"
          title="Baixar Histórico de Perfis"
        >
          <i :class="loadingExcel ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
        </button>
      </div>

      <!-- Sensores CAM / MIC -->
      <BarnSensorsStrip
        v-if="hasOtherSensors && !loadingSensor"
        :barn="barn"
        :batch="dataBatch"
        :batch-loading="loadingBatch"
        :cam-sensors="camSensors"
        :mic-sensors="micSensors"
        :sense-sensors="senseSensors"
      />

      <!-- Resumo total (oculto se houver só 1 matéria-prima — vai no bloco abaixo) -->
      <div
        v-if="feedSensors.length > 0 && !loadingSensor && !showCompactFoodSummary && hasSiloTotals"
        class="barn-totals-summary"
      >
        <div class="barn-totals-content">
          <span class="barn-totals-value">
            Peso total de {{ formatWeightDisplay(totalWeight) }}
            <span class="tons-icon tons-icon-inline" aria-hidden="true">
              <span class="tons-icon__ring"></span>
              <span class="tons-icon__body">T</span>
            </span>
          </span>
          <span class="barn-totals-sep" aria-hidden="true">|</span>
          <span class="barn-totals-value">
            Total de {{ formatNumber(totalBags, 0) }} sacas
            <span class="sacas-icon" aria-hidden="true">
              <span class="sacas-icon__beans"></span>
              <span class="sacas-icon__lip"></span>
              <span class="sacas-icon__body"></span>
            </span>
          </span>
        </div>
      </div>

      <!-- Resumo por matéria-prima (só aparece se show_silo_home estiver ativo) -->
      <div
        v-if="showSiloHome && feedSensors.length > 0 && !loadingSensor && totalsByFood.length > 0"
        class="barn-food-summary"
        :class="{ 'barn-food-summary--compact': showCompactFoodSummary }"
      >
        <div v-if="showCompactFoodSummary" class="barn-food-summary-compact">
          <i class="fas fa-seedling barn-food-summary-icon" aria-hidden="true"></i>
          <span class="barn-food-compact-name">{{ totalsByFood[0].name }}</span>
          <span class="barn-food-compact-sep" aria-hidden="true">|</span>
          <span class="barn-food-compact-value barn-food-compact-weight">
            {{ formatWeightDisplay(totalsByFood[0].weight) }}
            <span class="tons-icon tons-icon-inline" aria-hidden="true">
              <span class="tons-icon__ring"></span>
              <span class="tons-icon__body">T</span>
            </span>
          </span>
          <span class="barn-food-compact-sep" aria-hidden="true">|</span>
          <span class="barn-food-compact-value barn-food-compact-bags">
            {{ formatNumber(totalsByFood[0].bags, 0) }} sacas
            <span class="sacas-icon" aria-hidden="true">
              <span class="sacas-icon__beans"></span>
              <span class="sacas-icon__lip"></span>
              <span class="sacas-icon__body"></span>
            </span>
          </span>
        </div>
        <template v-else>
          <div class="barn-food-summary-header">
            <i class="fas fa-seedling barn-food-summary-icon"></i>
            <span class="barn-food-summary-title">Resumo por matéria-prima</span>
          </div>
          <div class="barn-food-summary-grid">
            <div
              v-for="item in totalsByFood"
              :key="item.name"
              class="barn-food-summary-item"
            >
              <div class="barn-food-item-header">
                <span class="barn-food-name">{{ item.name }}</span>
                <span class="barn-food-percent">{{ formatNumber(item.percent, 1) }}%</span>
              </div>
              <div class="barn-food-item-metrics">
                <span class="barn-food-metric barn-food-weight">
                  {{ formatWeightDisplay(item.weight) }}
                  <span class="tons-icon tons-icon-inline" aria-hidden="true">
                    <span class="tons-icon__ring"></span>
                    <span class="tons-icon__body">T</span>
                  </span>
                </span>
                <span class="barn-food-metric-sep" aria-hidden="true">·</span>
                <span class="barn-food-metric barn-food-bags">
                  {{ formatNumber(item.bags, 0) }} sacas
                  <span class="sacas-icon" aria-hidden="true">
                    <span class="sacas-icon__beans"></span>
                    <span class="sacas-icon__lip"></span>
                    <span class="sacas-icon__body"></span>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Loading placeholder (somente lista de sensores feed) -->
      <div v-if="loadingSensor" class="loading-container">
        <AnimadePlaceHolder height="120px" width="100%" borderRadius="4px"/>
      </div>

      <!-- Grid de Silos — cada card carrega e exibe o próprio dado -->
      <div
        v-if="!loadingSensor && feedSensors.length > 0"
        class="silos-grid"
        :class="{ 'silos-grid--five': feedSensors.length === 5 }"
      >
        <SensorFeedSiloHome
          v-for="sensor in feedSensors"
          :key="sensor.pk"
          :sensor="sensor"
          :barn="barn"
          :sensors="feedSensors"
          :batch="dataBatch"
          :farm-pk="farmPk"
          @silo-totals="onSiloTotals"
        />
      </div>

      <!-- Mensaje si no hay sensores feed -->
      <div v-if="!loadingSensor && feedSensors.length === 0" class="no-sensors-message">
        Não há sensores de alimentação neste galpão
      </div>

      <!-- Card con Tabs: gráfico de Entradas / Saídas (nível) del consolidado -->
      <div
        v-if="feedSensors.length > 0 && showInputOutputConsumeGraph"
        class="barn-inout-chart"
        :class="{ 'barn-inout-chart--expanded': chartSectionExpanded }"
      >
      <button
        type="button"
        class="chart-section-toggle"
        :title="chartSectionExpanded ? 'Ocultar gráfico' : 'Mostrar gráfico de entradas e saídas'"
        :aria-expanded="chartSectionExpanded"
        @click="chartSectionExpanded = !chartSectionExpanded"
      >
        <i class="fas fa-chart-bar chart-section-toggle__icon"></i>
        <span class="chart-section-toggle__label">Entradas / Saídas</span>
        <i
          class="chart-section-toggle__chevron fas"
          :class="chartSectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
        ></i>
      </button>

      <div v-show="chartSectionExpanded" class="chart-section-body">
        <div class="chart-filter-row">
          <label class="chart-filter-label">
            Silo:
            <select
              v-model="selectedSiloId"
              class="chart-filter-select"
              @change="rebuildChartFromFilter"
            >
              <option value="all">Todos</option>
              <option
                v-for="s in consolidatedSiloOrder"
                :key="s.id"
                :value="s.id"
              >
                {{ s.name }}
              </option>
            </select>
          </label>
          <ChartDateFilter
            :start-date="chartStartDate"
            :end-date="chartEndDate"
            :loading="loadingChartSearch"
            @search="onChartSearch"
          />
          <div class="chart-excel-wrapper">
            <button
              type="button"
              class="excel-button excel-button-chart"
              title="Exportar Consolidado (Excel/PDF)"
              :disabled="loadingExcel || feedSensors.length === 0"
              @click="openExportModalConsolidado"
            >
              <i :class="loadingExcel ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
            </button>
          </div>
        </div>

        <div v-if="consolidatedChartLabels.length > 0">
          <div class="tabs-header">
            <button
              type="button"
              class="tab-button"
              :class="{ active: activeConsolidatedTab === 'resumo' }"
              @click="activeConsolidatedTab = 'resumo'"
            >
              Resumo
            </button>
            <button
              type="button"
              class="tab-button"
              :class="{ active: activeConsolidatedTab === 'kg' }"
              @click="activeConsolidatedTab = 'kg'"
            >
              Entradas / Saídas
            </button>
          </div>

          <div class="tab-content">
            <!-- Tab Resumo: suma total de entradas e saídas (kg + sacas) -->
            <div v-if="activeConsolidatedTab === 'resumo'" class="tab-panel tab-panel-resumo">
              <div class="resumo-charts">
                <div class="resumo-chart-block">
                  <h5 class="resumo-chart-title">Resumo</h5>
                  <SilosInOutBarChart
                    :key="'resumo-' + chartRenderKey"
                    :chart-labels="resumoChartLabels"
                    :entries-data="[resumoTotalEntriesKgReal]"
                    :exits-data="[resumoTotalExitsKgReal]"
                    :entries-sacas-data="[resumoTotalEntriesSacasReal]"
                    :exits-sacas-data="[resumoTotalExitsSacasReal]"
                    :real-entries-kg="[resumoTotalEntriesKg]"
                    :real-exits-kg="[resumoTotalExitsKg]"
                    :real-entries-sacas="[resumoTotalEntriesSacas]"
                    :real-exits-sacas="[resumoTotalExitsSacas]"
                    unit="kg"
                  />
                </div>
              </div>
            </div>

            <!-- Tab kg: por intervalo -->
            <div v-if="activeConsolidatedTab === 'kg'" class="tab-panel">
              <SilosInOutBarChart
                :key="'kg-' + chartRenderKey"
                :chart-labels="consolidatedChartLabels"
                :tooltip-labels="consolidatedChartTooltipLabels"
                :entries-data="consolidatedEntriesKgReal"
                :exits-data="consolidatedExitsKgReal"
                :entries-sacas-data="consolidatedEntriesSacasReal"
                :exits-sacas-data="consolidatedExitsSacasReal"
                :real-entries-kg="consolidatedEntriesKg"
                :real-exits-kg="consolidatedExitsKg"
                :real-entries-sacas="consolidatedEntriesSacas"
                :real-exits-sacas="consolidatedExitsSacas"
                :net-entries-level="consolidatedNetEntriesLevel"
                :net-exits-level="consolidatedNetExitsLevel"
                :net-entries-kg="consolidatedNetEntriesKgReal"
                :net-exits-kg="consolidatedNetExitsKgReal"
                unit="kg"
              />
            </div>
          </div>
        </div>

        <div v-else class="chart-placeholder">
          <p v-if="loadingChartSearch">A carregar...</p>
          <p v-else>Selecione as datas e clique na lupa para buscar os dados (todos os silos).</p>
        </div>
      </div>
    </div>
    </div>

    <!-- Modal: Histórico de Perfis (botón Excel del título) -->
    <ExportProfileModal
      :is-open="showModal"
      :loading="loadingExcel"
      initial-tab="history"
      single-tab="history"
      @close="closeModal"
      @confirm="onExportConfirm"
    />

  </div>
</template>

<script>
import SensorFeedSiloHome from './SensorFeedSiloHome.vue';
import BarnSensorsStrip from './BarnSensorsStrip.vue';
import AnimadePlaceHolder from './AnimadePlaceHolder.vue';
import ExportProfileModal from './ExportProfileModal.vue';
import SilosInOutBarChart from './SilosInOutBarChart.vue';
import ChartDateFilter from './ChartDateFilter.vue';
import { apiRoutes } from "../utils/api"
import { normalizeSiloLastProfile } from '../utils/siloLevelToKg';
import { generateMultipleSilosExcel } from '../excel/excelSiloData';
import { generateMultipleSilosPDF } from '../pdf/pdfSiloData';
import { generateConsolidated8hPDF } from '../pdf/pdfConsolidated8h';
import { generateConsolidated8hRangeExcel } from '../excel/excelConsolidated8hRange';
import { buildConsolidatedDataForRange, withSacas } from '../utils/consolidatedDataByRange';
import {
  filterProfilesByMinLevelVariation,
  getMinSiloLevelVariationPercent,
} from '../utils/siloLevelVariationFilter';
import { format, parseISO, subDays } from 'date-fns';
import { isSmartSenseSensor } from '../constants/smartsense';
import { getFarmPk } from '../utils/siloHomeVisibility';
import { resolveFeedInventoryAccess } from '../utils/feedInventoryAccess';

export default {
  name: 'BarnSilos',
  components: {
    SensorFeedSiloHome,
    BarnSensorsStrip,
    AnimadePlaceHolder,
    ExportProfileModal,
    SilosInOutBarChart,
    ChartDateFilter
  },
  props: {
    barn: {
      type: Object,
      required: true
    },
    farm: {
      type: Object,
      required: true
    },
    hideHeader: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dataSensors: [],
      dataBatch: [],
      loadingSensor: true,
      loadingBatch: true,
      showBarnTitle: false,
      loadingExcel: false,
      showModal: false,
      modalInitialTab: 'history',
      modalSingleTab: null,
      consolidatedChartLabels: [],
      consolidatedChartTooltipLabels: [],
      consolidatedEntriesKg: [],
      consolidatedExitsKg: [],
      consolidatedEntriesSacas: [],
      consolidatedExitsSacas: [],
      consolidatedEntriesKgReal: [],
      consolidatedExitsKgReal: [],
      consolidatedEntriesSacasReal: [],
      consolidatedExitsSacasReal: [],
      consolidatedNetEntriesLevel: [],
      consolidatedNetExitsLevel: [],
      consolidatedNetEntriesKgReal: [],
      consolidatedNetExitsKgReal: [],
      activeConsolidatedTab: 'resumo',
      selectedSiloId: 'all',
      consolidatedSiloOrder: [],
      consolidatedIntervalsAll: [],
      consolidatedIntervalsPerSilo: [],
      lastConsolidatedRange: null,
      lastConsolidatedStart: null,
      lastConsolidatedEnd: null,
      chartStartDate: (() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 10);
      })(),
      chartEndDate: new Date().toISOString().slice(0, 10),
      loadingChartSearch: false,
      chartSectionExpanded: false,
      siloTotalsBySensor: {},
      // Incrementado a cada updateConsolidatedChart; usado como :key dos charts
      // para forçar re-montagem limpa do canvas (corrige caso em que o gráfico
      // não aparece ao voltar para a Home após visitar a página de detalhe).
      chartRenderKey: 0,
    }
  },
  computed: {
    farmPk() {
      return getFarmPk(this.farm);
    },
    totalWeight() {
      return Object.values(this.siloTotalsBySensor).reduce((sum, t) => sum + (Number(t.weight) || 0), 0);
    },
    totalBags() {
      return Object.values(this.siloTotalsBySensor).reduce((sum, t) => sum + (Number(t.bags) || 0), 0);
    },
    /**
     * Agrupa o peso e sacas por tipo de matéria-prima (food).
     * Silos sem food definido caem em "Não identificado".
     * Retorna array ordenado por peso desc.
     */
    totalsByFood() {
      const groups = {};
      Object.values(this.siloTotalsBySensor).forEach((t) => {
        const weight = Number(t.weight) || 0;
        const bags = Number(t.bags) || 0;
        if (weight <= 0) return;
        const rawName = (t.food && String(t.food).trim()) || 'Não identificado';
        // Chave normalizada: minúscula + colapso de espaços internos
        // para agrupar nomes equivalentes ("Milho em Grão" == "milho  em grão")
        const key = rawName.toLowerCase().replace(/\s+/g, ' ').trim();
        if (!groups[key]) groups[key] = { name: rawName, weight: 0, bags: 0 };
        groups[key].weight += weight;
        groups[key].bags += bags;
      });
      const items = Object.values(groups).sort((a, b) => b.weight - a.weight);
      const total = this.totalWeight;
      if (total <= 0) return items;
      return items.map((item) => ({
        ...item,
        percent: (item.weight / total) * 100
      }));
    },
    companySettings() {
      return this.$store.getters.getCompanySettings
    },
    sacaWeight() {
      return this.companySettings?.settings?.client_frontend?.weight_of_feed_bag ?? 60
    },
    showSiloHome() {
      return this.companySettings?.settings?.client_frontend?.show_silo_home ?? false
    },
    showInputOutputConsumeGraph() {
      return this.companySettings?.settings?.client_frontend?.show_input_output_consume_graph ?? false
    },
    showFeedInventoryLink() {
      return resolveFeedInventoryAccess(
        this.$store.getters.getCurrentUser,
        this.companySettings,
      ).allowed
    },
    minSignificantDeltaPercent() {
      const specific = this.companySettings?.settings?.client_frontend?.input_output_consume_graph_min_significant_delta_percent;
      if (specific != null && specific !== '' && Number.isFinite(Number(specific))) {
        return Number(specific);
      }
      return getMinSiloLevelVariationPercent(this.companySettings);
    },
    resumoChartLabels() {
      return ['Resumo']
    },
    resumoTotalEntriesKg() {
      return (this.consolidatedEntriesKg || []).reduce((sum, v) => sum + (Number(v) || 0), 0)
    },
    resumoTotalExitsKg() {
      return (this.consolidatedExitsKg || []).reduce((sum, v) => sum + (Number(v) || 0), 0)
    },
    resumoTotalEntriesSacas() {
      return (this.consolidatedEntriesSacas || []).reduce((sum, v) => sum + (Number(v) || 0), 0)
    },
    resumoTotalExitsSacas() {
      return (this.consolidatedExitsSacas || []).reduce((sum, v) => sum + (Number(v) || 0), 0)
    },
    resumoTotalEntriesKgReal() {
      return (this.consolidatedEntriesKgReal || []).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0
      )
    },
    resumoTotalExitsKgReal() {
      return (this.consolidatedExitsKgReal || []).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0
      )
    },
    resumoTotalEntriesSacasReal() {
      const totalKg = this.resumoTotalEntriesKgReal;
      const w = Number(this.sacaWeight) || 60;
      return totalKg / w;
    },
    resumoTotalExitsSacasReal() {
      const totalKg = this.resumoTotalExitsKgReal;
      const w = Number(this.sacaWeight) || 60;
      return totalKg / w;
    },
    camSensors() {
      return this.sensorsByType('smartcam');
    },
    micSensors() {
      return this.sensorsByType('smartmic', 'smartmic_central_V3');
    },
    senseSensors() {
      return (this.dataSensors || []).filter(isSmartSenseSensor);
    },
    hasOtherSensors() {
      return this.camSensors.length > 0
        || this.micSensors.length > 0
        || this.senseSensors.length > 0;
    },
    showCompactFoodSummary() {
      return this.showSiloHome && this.totalsByFood.length === 1;
    },
    hasSiloTotals() {
      return Object.keys(this.siloTotalsBySensor).length > 0;
    },
    feedSensors() {
      if (this.dataSensors.length > 0) {
        const smartfeedSensors = this.dataSensors.filter(sensor => sensor.type === 'smartfeed');
        const filteredSensors = this.filterFeedByVirtual(smartfeedSensors);
        

        return filteredSensors.sort((a, b) => {
          const siloNumberA = parseInt(a.name?.match(/\d+/)?.[0]);
          const siloNumberB = parseInt(b.name?.match(/\d+/)?.[0]);
          
          if (siloNumberA !== null && siloNumberB !== null) {
            return siloNumberA - siloNumberB;
          }
          
          if (siloNumberA !== null && siloNumberB === null) return -1;
          if (siloNumberA === null && siloNumberB !== null) return 1;
          
          return a.name.localeCompare(b.name);
        });
      }
      return [];
    },
    activeBatchPk() {
      const batch = Array.isArray(this.dataBatch) ? this.dataBatch[0] : null;
      if (!batch || batch.date_departure != null) return null;
      const pk = batch.pk ?? batch.id;
      return pk != null && pk !== '' ? pk : null;
    },
    barnTitle() {
      if (this.activeBatchPk != null) {
        return `${this.barn.name} · Lote ${this.activeBatchPk}`;
      }
      return this.barn.name;
    },
  },
  methods: {
    goToFeedInventoryAllBarns() {
      this.$router.push({ name: 'FeedInventoryAllBarnsPage' })
    },
    hydrateFromCache() {
      const cached = this.$store.getters.getCachedHomeBarnData(this.barn.pk);
      if (!cached) return false;

      this.dataSensors = Array.isArray(cached.sensors) ? cached.sensors : [];
      this.dataBatch = Array.isArray(cached.batch) ? cached.batch : [];
      this.loadingSensor = false;
      this.loadingBatch = false;
      return true;
    },
    async fetchBatch() {
      try {
        this.$store.commit('incrementActiveRequests');
        let url_to_fetch = apiRoutes.batchHomeByBarn(this.barn.pk);
        const response = await fetch(url_to_fetch, {
          headers: this.$store.getters.getHeader
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const json = await response.json();
        this.dataBatch = json;
        this.$store.commit('setHomeBarnDataCache', {
          barnPk: this.barn.pk,
          batch: json,
          lastUpdated: new Date().toISOString()
        });

        if (json.length > 0) {
          const oldBatch = json.map(element => {
            return {
              ...element,
              farm: this.farm,
              barn: this.barn
            }
          });
          this.$store.commit('setHistoryBatch', oldBatch);
        }
      } catch (error) {
        console.error('Error fetching batch:', error);
        this.dataBatch = [];
      } finally {
        this.$store.commit('decrementActiveRequests');
        this.loadingBatch = false;
      }
    },
    
    async sensorsFetch() {
      try {
        this.$store.commit('incrementActiveRequests');
        let url_to_fetch = apiRoutes.barnSensorsHome(this.barn.pk);
        const response = await fetch(url_to_fetch, {
          headers: this.$store.getters.getHeader
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        this.dataSensors = await response.json();
        this.$store.commit('setHomeBarnDataCache', {
          barnPk: this.barn.pk,
          sensors: this.dataSensors,
          lastUpdated: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error fetching sensors:', error);
        this.dataSensors = [];
      } finally {
        this.$store.commit('decrementActiveRequests');
        this.loadingSensor = false;
      }
    },
    
    filterFeedByVirtual(sensors) {
      const siloGroups = {};
      sensors.forEach(sensor => {
        if (!siloGroups[sensor.silo]) {
          siloGroups[sensor.silo] = [];
        }
        siloGroups[sensor.silo].push(sensor);
      });
      
      return sensors.filter(sensor => {
        const siloGroup = siloGroups[sensor.silo];
        return siloGroup.length === 1 || sensor.virtual;
      });
    },

    sensorsByType(type, type2) {
      if (this.dataSensors.length === 0) return [];
      if (type === 'smartmic') {
        return this.dataSensors.filter(sensor => sensor.type === type || sensor.type === type2);
      }
      return this.dataSensors.filter(sensor => sensor.type === type);
    },

    downloadProfileReport() {
      if (this.feedSensors.length === 0) {
        alert('Não há sensores de alimentação disponíveis');
        return;
      }
      this.modalInitialTab = 'history';
      this.modalSingleTab = 'history';
      this.showModal = true;
    },

    openExportModalConsolidado() {
      if (this.feedSensors.length === 0) {
        alert('Não há sensores de alimentação disponíveis');
        return;
      }
      if (!this.chartStartDate || !this.chartEndDate) {
        alert('Selecione Data Início e Data Fim.');
        return;
      }
      this.onExportConfirm({
        type: 'consolidated',
        startDate: this.chartStartDate,
        endDate: this.chartEndDate,
        format: 'excel',
        removeOutlierLimit: false,
        intervalHours: 24,
        minSignificantPercent: this.minSignificantDeltaPercent
      });
    },

    closeModal() {
      this.showModal = false;
      this.loadingExcel = false;
      this.modalInitialTab = 'history';
      this.modalSingleTab = null;
    },

    closeConsolidadoModal() {
      this.loadingExcel = false;
    },

    async onExportConfirm(payload) {
      const { type, startDate, endDate, format: exportFormat, removeOutlierLimit, intervalHours, minSignificantPercent } = payload;
      try {
        this.loadingExcel = true;
        const locationInfo = {
          farmName: this.farm?.name || 'Granja',
          barnName: this.barn?.name || 'Galpão'
        };

        if (type === 'consolidated') {
          let rangeDataWithSacas = null;

          const useCached =
            !removeOutlierLimit &&
            (intervalHours == null || Number(intervalHours) === 24) &&
            this.lastConsolidatedRange &&
            this.lastConsolidatedStart === startDate &&
            this.lastConsolidatedEnd === endDate;

          if (useCached) {
            rangeDataWithSacas = this.lastConsolidatedRange;
          } else {
            rangeDataWithSacas = await this.buildConsolidatedRangeData(startDate, endDate, {
              applyOutlierFilter: !removeOutlierLimit,
              intervalHours: intervalHours != null ? intervalHours : 24,
              minSignificantDeltaPercent: minSignificantPercent != null ? minSignificantPercent : this.minSignificantDeltaPercent,
            });
            if (!rangeDataWithSacas) {
              alert('Não há dados de perfis disponíveis para o período selecionado');
              return;
            }
            this.lastConsolidatedRange = rangeDataWithSacas;
            this.lastConsolidatedStart = startDate;
            this.lastConsolidatedEnd = endDate;
          }

          if (!rangeDataWithSacas.siloOrder.length && (!rangeDataWithSacas.intervals8h || !rangeDataWithSacas.intervals8h.length) && (!rangeDataWithSacas.dailySummaries || !rangeDataWithSacas.dailySummaries.length)) {
            alert('Não foi possível gerar o consolidado para o período selecionado');
            return;
          }

          // Actualizar gráfico con los mismos dados usados para o consolidado
          this.updateConsolidatedChart(rangeDataWithSacas);

          if (exportFormat === 'pdf') {
            const { profilesBySilo } = await this.fetchProfilesBySiloForConsolidated(startDate, endDate);
            if (!profilesBySilo || Object.keys(profilesBySilo).length === 0) {
              alert('Não há dados de perfis disponíveis para o período selecionado');
              return;
            }
            const singleInterval = this.buildConsolidatedData(profilesBySilo, startDate);
            const effectiveEnd = singleInterval.endDate;
            generateConsolidated8hPDF(singleInterval, locationInfo, startDate, effectiveEnd);
          } else {
            const hours = intervalHours != null ? intervalHours : 24;
            await generateConsolidated8hRangeExcel(rangeDataWithSacas, locationInfo, startDate, endDate, this.sacaWeight, hours);
          }
        } else {
          const realDataBySilo = await this.fetchProfilesForExport(startDate, endDate);
          if (!realDataBySilo || Object.keys(realDataBySilo).length === 0) {
            alert('Não há dados de perfis disponíveis para o período selecionado');
            return;
          }
          if (exportFormat === 'pdf') {
            await generateMultipleSilosPDF(realDataBySilo, locationInfo);
          } else {
            await generateMultipleSilosExcel(realDataBySilo, locationInfo);
          }
        }
        this.closeModal();
        this.closeConsolidadoModal();
      } catch (error) {
        console.error('Error al generar archivo:', error);
        alert(error.message || 'Erro ao gerar o arquivo');
      } finally {
        this.loadingExcel = false;
      }
    },

    buildConsolidatedData(profilesBySilo, startDate) {
      const [sy, sm, sd] = startDate.substring(0, 10).split('-').map(Number);
      const startDt = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
      const maxEnd = new Date(startDt.getTime() + 8 * 60 * 60 * 1000);
      const endDt = maxEnd;
      const silos = {};
      const siloOrder = Object.keys(profilesBySilo).map(id => ({
        id,
        name: (profilesBySilo[id][0] && (profilesBySilo[id][0].silo_name || `Silo ${id}`)) || `Silo ${id}`
      }));
      siloOrder.forEach(({ id, name }) => {
        silos[id] = { siloName: name, subida: 0, descida: 0 };
      });
      Object.entries(profilesBySilo).forEach(([siloId, profiles]) => {
        const filtered = profiles.filter(p => {
          const t = new Date(p.datetime_fetched);
          return t >= startDt && t <= endDt;
        });
        const sorted = [...filtered].sort((a, b) => new Date(a.datetime_fetched) - new Date(b.datetime_fetched));
        const minVariation = this.minSignificantDeltaPercent;
        for (let i = 0; i < sorted.length - 1; i++) {
          const w0 = sorted[i].weight != null ? Number(sorted[i].weight) : null;
          const w1 = sorted[i + 1].weight != null ? Number(sorted[i + 1].weight) : null;
          if (w0 == null || w1 == null) continue;
          const level0 = sorted[i].level != null ? Number(sorted[i].level) : null;
          const level1 = sorted[i + 1].level != null ? Number(sorted[i + 1].level) : null;
          if (level0 != null && level1 != null && minVariation > 0 && Math.abs(level1 - level0) <= minVariation) {
            continue;
          }
          const delta = w1 - w0;
          if (delta > 0) silos[siloId].subida += delta;
          else if (delta < 0) silos[siloId].descida += Math.abs(delta);
        }
      });
      return { silos, siloOrder, endDate: endDt.toISOString() };
    },

    updateConsolidatedChart(rangeDataWithSacas) {
      const intervalsAll = (rangeDataWithSacas && rangeDataWithSacas.intervals8h) || [];
      const siloOrder = (rangeDataWithSacas && rangeDataWithSacas.siloOrder) || [];
      const intervalsPerSilo = (rangeDataWithSacas && rangeDataWithSacas.intervalsPerSilo) || [];

      if (!intervalsAll.length) {
        this.consolidatedChartLabels = [];
        this.consolidatedChartTooltipLabels = [];
        this.consolidatedEntriesKg = [];
        this.consolidatedExitsKg = [];
        this.consolidatedEntriesSacas = [];
        this.consolidatedExitsSacas = [];
        this.consolidatedEntriesKgReal = [];
        this.consolidatedExitsKgReal = [];
        this.consolidatedEntriesSacasReal = [];
        this.consolidatedExitsSacasReal = [];
        this.consolidatedNetEntriesLevel = [];
        this.consolidatedNetExitsLevel = [];
        this.consolidatedNetEntriesKgReal = [];
        this.consolidatedNetExitsKgReal = [];
        this.consolidatedSiloOrder = [];
        this.consolidatedIntervalsAll = [];
        this.consolidatedIntervalsPerSilo = [];
        this.selectedSiloId = 'all';
        return;
      }

      this.consolidatedSiloOrder = siloOrder;
      this.consolidatedIntervalsAll = intervalsAll;
      this.consolidatedIntervalsPerSilo = intervalsPerSilo;
      this.selectedSiloId = 'all';
      this.rebuildChartFromFilter();
      this.chartRenderKey += 1;
    },

    getIntervalsForCurrentFilter() {
      if (this.selectedSiloId === 'all' || this.selectedSiloId == null || this.selectedSiloId === '') {
        return this.consolidatedIntervalsAll || [];
      }
      const siloData = (this.consolidatedIntervalsPerSilo || []).find(
        s => String(s.siloId) === String(this.selectedSiloId)
      );
      return siloData ? siloData.intervals : [];
    },

    rebuildChartFromFilter() {
      const intervals = this.getIntervalsForCurrentFilter();
      if (!intervals.length) {
        this.consolidatedChartLabels = [];
        this.consolidatedChartTooltipLabels = [];
        this.consolidatedEntriesKg = [];
        this.consolidatedExitsKg = [];
        this.consolidatedEntriesSacas = [];
        this.consolidatedExitsSacas = [];
        this.consolidatedNetEntriesLevel = [];
        this.consolidatedNetExitsLevel = [];
        this.consolidatedNetEntriesKgReal = [];
        this.consolidatedNetExitsKgReal = [];
        this.lastConsolidatedRange = null;
        this.lastConsolidatedStart = null;
        this.lastConsolidatedEnd = null;
        return;
      }

      // Cada barra representa um dia inteiro (00:00 - 23:59), então no eixo X
      // mostramos apenas a data — curta, sem rotação forçada e sem cortes.
      // O intervalo completo (00:00 - 23:59) continua disponível no tooltip.
      this.consolidatedChartLabels = intervals.map(interval => {
        try {
          const start = parseISO(interval.start);
          return format(start, 'dd/MM');
        } catch (e) {
          return interval.start;
        }
      });

      this.consolidatedChartTooltipLabels = intervals.map(interval => {
        try {
          const start = parseISO(interval.start);
          const end = parseISO(interval.end);
          const endAdj = new Date(end.getTime() - 60 * 1000); // 1 minuto antes
          return `${format(start, 'dd/MM/yyyy')} (${format(start, 'HH:mm')} - ${format(endAdj, 'HH:mm')})`;
        } catch (e) {
          return `${interval.start} - ${interval.end}`;
        }
      });

      this.consolidatedEntriesKg = intervals.map(interval => {
        const v = Number((interval.entradasLevel != null ? interval.entradasLevel : interval.entradasKg) || 0);
        return Number.isFinite(v) ? v : 0;
      });

      this.consolidatedExitsKg = intervals.map(interval => {
        const v = Number((interval.saidasLevel != null ? interval.saidasLevel : interval.saidasKg) || 0);
        return Number.isFinite(v) ? v : 0;
      });

      this.consolidatedEntriesSacas = intervals.map(interval => {
        const v = Number(interval.entradasSacas || 0);
        return Number.isFinite(v) ? v : 0;
      });

      this.consolidatedExitsSacas = intervals.map(interval => {
        const v = Number(interval.saidasSacas || 0);
        return Number.isFinite(v) ? v : 0;
      });

      this.consolidatedEntriesKgReal = intervals.map(interval => {
        const v = Number(interval.entradasKgReal || 0);
        return Number.isFinite(v) ? v : 0;
      });

      this.consolidatedExitsKgReal = intervals.map(interval => {
        const v = Number(interval.saidasKgReal || 0);
        return Number.isFinite(v) ? v : 0;
      });

      this.consolidatedEntriesSacasReal = this.consolidatedEntriesKgReal.map(v => {
        const n = Number(v);
        return Number.isFinite(n) ? n / this.sacaWeight : 0;
      });

      this.consolidatedExitsSacasReal = this.consolidatedExitsKgReal.map(v => {
        const n = Number(v);
        return Number.isFinite(n) ? n / this.sacaWeight : 0;
      });

      this.consolidatedNetEntriesLevel = intervals.map(interval => Number(interval.netEntradasLevel ?? 0) || 0);
      this.consolidatedNetExitsLevel = intervals.map(interval => Number(interval.netSaidasLevel ?? 0) || 0);
      this.consolidatedNetEntriesKgReal = intervals.map(interval => Number(interval.netEntradasKgReal ?? 0) || 0);
      this.consolidatedNetExitsKgReal = intervals.map(interval => Number(interval.netSaidasKgReal ?? 0) || 0);
    },

    async loadDefaultConsolidatedChart() {
      try {
        const startDate = this.chartStartDate || format(subDays(new Date(), 7), 'yyyy-MM-dd');
        const endDate = this.chartEndDate || format(new Date(), 'yyyy-MM-dd');

        const rangeDataWithSacas = await this.buildConsolidatedRangeData(startDate, endDate);
        if (!rangeDataWithSacas) {
          this.updateConsolidatedChart({ intervals8h: [] });
          return;
        }
        this.updateConsolidatedChart(rangeDataWithSacas);
        this.lastConsolidatedRange = rangeDataWithSacas;
        this.lastConsolidatedStart = startDate;
        this.lastConsolidatedEnd = endDate;
        this.$nextTick(() => {
          this.chartStartDate = startDate;
          this.chartEndDate = endDate;
        });
      } catch (error) {
        console.error('Error loading default consolidated chart:', error);
        this.updateConsolidatedChart({ intervals8h: [] });
      }
    },

    onChartSearch(startDate, endDate) {
      if (startDate && endDate) this.fetchChartByDateRange(startDate, endDate);
    },

    async fetchChartByDateRange(optionalStart, optionalEnd) {
      const startDate = (optionalStart != null ? optionalStart : this.chartStartDate || '').toString().trim().slice(0, 10);
      const endDate = (optionalEnd != null ? optionalEnd : this.chartEndDate || '').toString().trim().slice(0, 10);
      if (!startDate || !endDate) {
        alert('Selecione Data Início e Data Fim.');
        return;
      }
      if (startDate > endDate) {
        alert('Data Início deve ser anterior ou igual a Data Fim.');
        return;
      }

      try {
        this.loadingChartSearch = true;
        const rangeDataWithSacas = await this.buildConsolidatedRangeData(startDate, endDate);
        if (!rangeDataWithSacas) {
          this.updateConsolidatedChart({ intervals8h: [] });
          return;
        }
        this.updateConsolidatedChart(rangeDataWithSacas);
        this.lastConsolidatedRange = rangeDataWithSacas;
        this.lastConsolidatedStart = startDate;
        this.lastConsolidatedEnd = endDate;
        this.$nextTick(() => {
          this.chartStartDate = startDate;
          this.chartEndDate = endDate;
        });
      } catch (error) {
        console.error('Error fetching chart by date range:', error);
        alert(error.message || 'Erro ao buscar dados.');
      } finally {
        this.loadingChartSearch = false;
      }
    },

    async buildConsolidatedRangeData(startDate, endDate, options = {}) {
      const { profilesBySilo, foods } = await this.fetchProfilesBySiloForConsolidated(startDate, endDate);
      if (!profilesBySilo || Object.keys(profilesBySilo).length === 0) {
        return null;
      }

      const siloIds = Object.keys(profilesBySilo);
      const siloCurvesBySiloId = await this.fetchSiloCurvesBySiloId(siloIds);

      const rangeData = buildConsolidatedDataForRange(profilesBySilo, startDate, endDate, {
        applyOutlierFilter: options.applyOutlierFilter !== false,
        applyStabilityFilter: options.applyStabilityFilter !== false,
        intervalHours: options.intervalHours != null ? options.intervalHours : 24,
        minSignificantDeltaPercent: options.minSignificantDeltaPercent != null
          ? options.minSignificantDeltaPercent
          : this.minSignificantDeltaPercent,
        siloCurvesBySiloId,
        defaultDensity: 750,
        foods,
      });

      return withSacas(rangeData, this.sacaWeight);
    },

    async fetchProfilesBySiloForConsolidated(startDate, endDate) {
      const start = startDate && startDate.length >= 10 ? startDate.substring(0, 10) : startDate;
      const end = endDate && endDate.length >= 10 ? endDate.substring(0, 10) : endDate;
      const barnPk = this.barn?.pk ?? this.barn?.id;
      let companyPk = null;
      if (this.farm) {
        const fc = this.farm.farm?.company ?? this.farm.company;
        companyPk = fc?.pk ?? fc?.id ?? fc;
      }
      if (!companyPk) {
        const user = this.$store.getters.getCurrentUser;
        companyPk = user?.company?.pk ?? user?.company?.id ?? user?.company;
      }
      if (!barnPk || !companyPk) {
        throw new Error('Dados do galpão ou empresa não disponíveis');
      }
      const result = await this.$store.dispatch('fetchBarnProfilesHistory', {
        barn_pk: barnPk,
        start_date: start,
        end_date: end,
        company_pk: companyPk
      });
      const { profilesData, foods } = result;
      if (!profilesData || profilesData.length === 0 || profilesData.every(arr => !arr || arr.length === 0)) {
        return { profilesBySilo: {}, foods: foods || [] };
      }
      const allProfiles = profilesData.flat().filter(Boolean);
      const profilesBySilo = {};
      allProfiles.forEach(profile => {
        const siloId = profile.silo;
        if (!profilesBySilo[siloId]) profilesBySilo[siloId] = [];
        profilesBySilo[siloId].push(profile);
      });
      return { profilesBySilo, foods: foods || [] };
    },

    /** Fetch last profile per silo (with curves) for "Silo dimensions" → kg from level */
    async fetchSiloCurvesBySiloId(siloIds) {
      if (!siloIds || siloIds.length === 0) return {};
      const headers = this.$store.getters.getHeader;
      const results = await Promise.all(
        siloIds.map(async (siloId) => {
          try {
            const res = await fetch(apiRoutes.siloLastProfileBySilo(siloId, undefined, undefined, true), { headers });
            if (!res.ok) return { siloId, curves: null };
            const data = normalizeSiloLastProfile(await res.json());
            const curves = data?.curves?.length ? data.curves : null;
            return { siloId, curves };
          } catch (e) {
            console.warn('fetchSiloCurvesBySiloId failed for silo', siloId, e);
            return { siloId, curves: null };
          }
        })
      );
      const map = {};
      results.forEach(({ siloId, curves }) => {
        if (curves) map[siloId] = curves;
      });
      return map;
    },

    async fetchProfilesForExport(startDate, endDate) {
      const start = startDate && startDate.length >= 10 ? startDate.substring(0, 10) : startDate;
      const end = endDate && endDate.length >= 10 ? endDate.substring(0, 10) : endDate;
      const barnPk = this.barn?.pk ?? this.barn?.id;
      let companyPk = null;
      if (this.farm) {
        const fc = this.farm.farm?.company ?? this.farm.company;
        companyPk = fc?.pk ?? fc?.id ?? fc;
      }
      if (!companyPk) {
        const user = this.$store.getters.getCurrentUser;
        companyPk = user?.company?.pk ?? user?.company?.id ?? user?.company;
      }
      if (!barnPk || !companyPk) {
        throw new Error('Dados do galpão ou empresa não disponíveis');
      }
      const result = await this.$store.dispatch('fetchBarnProfilesHistory', {
        barn_pk: barnPk,
        start_date: start,
        end_date: end,
        company_pk: companyPk
      });
      const { profilesData, foods } = result;
      if (!profilesData || profilesData.length === 0 || profilesData.every(arr => !arr || arr.length === 0)) {
        return {};
      }
      const allProfiles = profilesData.flat().filter(Boolean);
      const profilesBySilo = {};
      allProfiles.forEach(profile => {
        const siloId = profile.silo;
        if (!profilesBySilo[siloId]) profilesBySilo[siloId] = [];
        profilesBySilo[siloId].push(profile);
      });
      const realDataBySilo = {};
      const stabilityFilterThreshold = 5;
      Object.entries(profilesBySilo).forEach(([siloId, profiles]) => {
        const sorted = profiles.sort((a, b) => new Date(a.datetime_fetched) - new Date(b.datetime_fetched));
        const stableData = sorted.filter((profile, idx, array) => {
          if (profile.level == null || profile.level <= 0) return false;
          if ([array[idx - 1], array[idx - 5], array[idx - 9]].includes(undefined)) return true;
          const diffBehind = (i) => Math.abs(profile.level - array[idx - i].level);
          return [diffBehind(1), diffBehind(5), diffBehind(9)].every(diff => diff < stabilityFilterThreshold);
        });
        const filteredData = filterProfilesByMinLevelVariation(
          stableData,
          getMinSiloLevelVariationPercent(this.companySettings)
        );
        const siloName = sorted[0]?.silo_name || `Silo ${siloId}`;
        realDataBySilo[siloName] = filteredData.map(row => ({
          ...row,
          food_name: this.getFoodNameFromFoods(row.food, foods),
          food_density: this.getFoodDensityFromFoods(row.food, foods)
        }));
      });
      return realDataBySilo;
    },

    getFoodNameFromFoods(foodId, foods) {
      if (!foods || !Array.isArray(foods)) return 'N/A';
      const food = foods.find(f => f.pk === foodId);
      return food ? food.name : 'N/A';
    },
    getFoodDensityFromFoods(foodId, foods) {
      if (!foods || !Array.isArray(foods)) return 'N/A';
      const food = foods.find(f => f.pk === foodId);
      return food && food.density != null ? food.density : 'N/A';
    },

    onDownloadError(error) {
      console.error('Erro ao baixar:', error);
    },

    onSiloTotals({ sensorPk, weight, bags, food }) {
      this.siloTotalsBySensor = {
        ...this.siloTotalsBySensor,
        [sensorPk]: { weight, bags, food: food || null }
      };
    },

    formatNumber(value, maxDecimals = 3) {
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: maxDecimals });
    },

    /** Peso: "X toneladas" si >= 1000 kg, "X kg" si no (sin decimales) */
    formatWeightDisplay(weightKg) {
      const w = Number(weightKg) || 0;
      if (w >= 1000) {
        const tons = Math.round(w / 1000);
        return this.formatNumber(tons, 0) + ' toneladas';
      }
      return this.formatNumber(Math.round(w), 0) + ' kg';
    }
  },
  watch: {
    'barn.pk'() {
      this.siloTotalsBySensor = {};
    },
    chartSectionExpanded(expanded) {
      if (expanded && this.showInputOutputConsumeGraph && !this.lastConsolidatedRange) {
        this.loadDefaultConsolidatedChart();
      }
    },
  },
  async created() {
    const hasCache = this.hydrateFromCache();
    void this.fetchBatch();

    if (hasCache) {
      void this.sensorsFetch();
      return;
    }

    await this.sensorsFetch();
  }
};
</script>

<style scoped>
.barn-silos-section {
  padding: 12px 20px;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.barn-silos-section--compact {
  padding-top: 8px;
}

.barn-silos-section:last-child {
  border-bottom: none;
}

.barn-title-container {
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.barn-actions-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.barn-title-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.feed-inventory-button {
  background-color: #78b843;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.feed-inventory-button:hover {
  background-color: #689f38;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.feed-inventory-button:active {
  transform: translateY(0);
}

.barn-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.barn-totals-summary {
  margin-bottom: 12px;
  padding: 10px 14px;
  background: linear-gradient(180deg, #edf6e6 0%, #e3f0d8 100%);
  border: 1px solid #b8d4a0;
  border-left: 4px solid #1f7a1f;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  text-align: center;
}

.barn-totals-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-weight: 600;
  color: #1b4238;
}

.barn-totals-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.barn-totals-label {
  font-weight: 600;
  margin-right: 8px;
}

.tons-icon {
  position: relative;
  width: 22px;
  height: 24px;
  display: inline-flex;
  justify-content: center;
  flex-shrink: 0;
}

.tons-icon-inline {
  margin: 0 6px 0 4px;
  vertical-align: middle;
  transform: translateY(-1px);
}

.tons-icon__ring {
  position: absolute;
  top: -1px;
  width: 10px;
  height: 7px;
  border: 2px solid #808b98;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
}

.tons-icon__body {
  position: absolute;
  top: 5px;
  width: 22px;
  height: 18px;
  border-radius: 3px 3px 5px 5px;
  border: 1px solid #9ca3af;
  background: linear-gradient(180deg, #f5f6f8 0%, #e5e7eb 100%);
  color: #207548;
  font-size: 12px;
  font-weight: 800;
  line-height: 18px;
  text-align: center;
  box-shadow: inset -3px 0 0 rgba(148, 163, 184, 0.2);
}

.barn-totals-sep {
  margin: 0 8px;
  color: #6b8f5e;
}

.sacas-icon {
  position: relative;
  width: 22px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 7px;
  vertical-align: middle;
  transform: translateY(-2px);
}

.sacas-icon__body {
  position: absolute;
  bottom: 1px;
  width: 18px;
  height: 16px;
  border: 1px solid #a0885d;
  border-radius: 4px 4px 5px 5px;
  background:
    linear-gradient(90deg, rgba(255,255,255,0.12) 0 1px, transparent 1px 3px) 0 0 / 3px 3px,
    linear-gradient(180deg, #e7d6ae 0%, #dbc79d 100%);
  box-shadow:
    inset -2px 0 0 rgba(129, 110, 73, 0.15),
    inset 0 2px 0 rgba(255,255,255,0.2);
}

.sacas-icon__lip {
  position: absolute;
  top: 6px;
  width: 20px;
  height: 5px;
  border: 1px solid #9f8658;
  border-radius: 4px;
  background:
    linear-gradient(90deg, rgba(255,255,255,0.15) 0 1px, transparent 1px 3px) 0 0 / 3px 3px,
    linear-gradient(180deg, #e9d9b2 0%, #d8c297 100%);
  z-index: 2;
}

.sacas-icon__beans {
  position: absolute;
  left: 2px;
  top: 0px;
  width: 18px;
  height: 8px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 3px 4px, #ead5ad 2px, transparent 2.2px),
    radial-gradient(circle at 7px 2px, #dec89f 2px, transparent 2.2px),
    radial-gradient(circle at 10px 5px, #e9d4ad 2px, transparent 2.2px),
    radial-gradient(circle at 14px 3px, #dcc69c 2px, transparent 2.2px),
    radial-gradient(circle at 16px 6px, #ecd8b1 2px, transparent 2.2px);
  z-index: 3;
}

.barn-food-summary {
  margin-bottom: 12px;
  padding: 12px 14px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.barn-food-summary--compact {
  margin-bottom: 10px;
  padding: 8px 12px;
  background: linear-gradient(180deg, #edf6e6 0%, #e3f0d8 100%);
  border: 1px solid #b8d4a0;
  border-left: 4px solid #1f7a1f;
}

.barn-food-summary-compact {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  font-size: 13px;
  line-height: 1.2;
}

.barn-food-compact-name {
  font-weight: 600;
  color: #1b4238;
}

.barn-food-compact-value {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.barn-food-compact-weight {
  font-weight: 600;
  color: #1f7a1f;
}

.barn-food-compact-bags {
  font-weight: 500;
  color: #4b5563;
}

.barn-food-compact-sep {
  color: #9ca3af;
  font-weight: 400;
  user-select: none;
}

.barn-food-summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #e5e7eb;
}

.barn-food-summary-icon {
  color: #1F7A1F;
  font-size: 13px;
  flex-shrink: 0;
}

.barn-food-summary-title {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.barn-food-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

.barn-food-summary-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 10px 12px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #1f7a1f;
  border-radius: 6px;
}

.barn-food-item-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.barn-food-item-metrics {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
}

.barn-food-metric {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.barn-food-metric-sep {
  color: #d1d5db;
  font-weight: 600;
  line-height: 1;
}

.barn-food-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #1b4238;
  line-height: 1.3;
  word-break: break-word;
}

.barn-food-percent {
  flex-shrink: 0;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 700;
  color: #166016;
  background: #edf6e6;
  border-radius: 999px;
  line-height: 1.3;
}

.barn-food-weight {
  font-size: 12px;
  font-weight: 600;
  color: #1f7a1f;
  line-height: 1.2;
}

.barn-food-bags {
  font-size: 12px;
  color: #4b5563;
  font-weight: 500;
  line-height: 1.2;
}

.barn-food-summary-item .tons-icon-inline {
  margin: 0 0 0 2px;
}

.barn-food-summary-item .sacas-icon {
  margin-left: 2px;
}

@media screen and (min-width: 768px) {
  .barn-food-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (min-width: 1200px) {
  .barn-food-summary-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.excel-button {
  background-color: #1F7A1F;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.excel-button:hover:not(:disabled) {
  background-color: #166016;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.excel-button:active:not(:disabled) {
  transform: translateY(0);
}

.excel-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.loading-container {
  padding: 8px 0;
}

.silos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  justify-content: start;
}

.silos-grid--five {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.silos-grid--five > * {
  min-width: 0;
}

.silos-grid > * {
  display: flex;
}

.barn-inout-chart {
  margin-top: 16px;
  margin-bottom: 0;
  padding: 12px 20px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-sizing: border-box;
}

.barn-inout-chart--expanded {
  padding-bottom: 20px;
  /* Altura mínima em vez de fixa: a linha de filtros pode quebrar em 2
     linhas em cards estreitos, e uma altura fixa cortava o rótulo do
     eixo X (o card tem overflow: hidden no componente pai). Com altura
     automática o card cresce para caber todo o conteúdo, sem cortes. */
  min-height: 520px;
  height: auto;
}

.chart-section-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 0;
  border: none;
  background: none;
  color: #1b4238;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: color 0.2s;
}

.chart-section-toggle:hover {
  color: #2d5a4a;
}

.chart-section-toggle__icon {
  font-size: 16px;
}

.chart-section-toggle__label {
  flex: 1;
}

.chart-section-toggle__chevron {
  font-size: 12px;
  color: #6b7280;
}

.chart-section-body {
  margin-top: 12px;
}

.chart-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px 16px;
  margin-bottom: 12px;
}

.chart-filter-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.chart-filter-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background-color: #fff;
  min-width: 160px;
}

.chart-filter-select:focus {
  outline: none;
  border-color: #1b4238;
}

.chart-filter-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background-color: #fff;
  min-width: 140px;
}

.chart-filter-input:focus {
  outline: none;
  border-color: #1b4238;
}

.chart-search-btn {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #1b4238;
  border-radius: 6px;
  background-color: #1b4238;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s, opacity 0.2s;
}

.chart-search-btn:hover:not(:disabled) {
  background-color: #2d5a4a;
}

.chart-search-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chart-excel-wrapper {
  margin-left: auto;
  display: flex;
  align-items: center;
}

/* Botón Excel al lado de la lupa: mismo color y border-radius que el de arriba, un poco más pequeño */
.excel-button-chart {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  padding: 0;
  border-radius: 6px;
  background-color: #1F7A1F;
  color: #fff;
  font-size: 15px;
}

.excel-button-chart:hover:not(:disabled) {
  background-color: #166016;
}

.excel-button-chart i {
  color: #fff;
}

.chart-placeholder {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.tabs-header {
  display: flex;
  gap: 0;
  width: 100%;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 8px;
}

.tab-button {
  flex: 1;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background-color 0.2s;
  text-align: center;
}

.tab-button.active {
  color: #1b4238;
  border-bottom-color: #1b4238;
  background-color: #f9fafb;
}

.tab-content {
  /* Altura definida pelo conteúdo (o chart tem sua própria altura fixa),
     já que o card pai agora usa altura automática. */
  height: auto;
}

.tab-panel {
  height: auto;
}

.tab-panel-resumo {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.resumo-charts {
  /* Um único gráfico ocupando toda a largura do card */
  display: block;
  min-height: 280px;
}

.resumo-chart-block {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.resumo-chart-title {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

/* Para pantallas pequeñas, centrar las tarjetas */
@media screen and (max-width: 1100px) {
  .silos-grid--five {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
}

@media screen and (max-width: 767px) {
  .silos-grid {
    justify-content: center;
  }

  .silos-grid--five {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }

  .barn-totals-content {
    flex-direction: column;
    gap: 6px;
  }

  .barn-totals-sep {
    display: none;
  }

  .barn-totals-value {
    white-space: normal;
  }
}

.no-sensors-message {
  padding: 40px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
}
</style>